import { FC, useMemo } from 'react';
import ContactBoxout, {
  HelpOption,
} from 'react-components/contact-boxout/contact-boxout';
import ContactListing, {
  Contact,
} from 'wa-storybook/components/contacts/contact-listing/contact-listing';
import {
  ContentWarning,
  WarningTypes,
} from 'wa-storybook/components/general/content-warning/content-warning';
import ContactItem from 'react-components/contact-boxout/item';
import {
  getExternalLinks,
  setDescriptiveAriaLabels,
} from 'pages/employee-assistance/containers/article-container/article-container.helpers';
import ModalityCard from 'react-components/modalities/modality-card/modality-card';
import styles from './cards.module.scss';
import { getPhoneNumberLabel } from 'pages/employee-assistance/employee-assistance.helpers';

type Props = {
  phoneNumbers: Contact[];
  contactOptions: HelpOption[];
  globalPhoneNumbers?: boolean;
  isMobile?: boolean;
  withDropdown?: boolean;
  phoneAnalytics?: () => void;
};

const dropdownId = 'feed-contact-numbers-dropdown-new';
const dropdownListId = 'feed-contact-numbers-dropdown-list-new';

const getPhoneLabels = (phoneNumbers: Array<Contact>) => {
  return phoneNumbers.map<Contact>((item: Contact) => {
    return { ...item, label_value: getPhoneNumberLabel(item) };
  });
};

const ContactListingCard: FC<Props> = ({
  phoneNumbers,
  globalPhoneNumbers,
  isMobile,
  withDropdown,
  contactOptions,
  phoneAnalytics,
}: Props) => {
  const hasPhoneNumbers = phoneNumbers.length > 0;
  const title = polyglot.t('employee_assistance.let_us_help.contact_telus');

  const mappedPhoneNumbers = useMemo(() => {
    return getPhoneLabels(phoneNumbers);
  }, [phoneNumbers]);

  return (
    <ModalityCard
      shouldGrow={true}
      title={withDropdown ? '' : title}
      className={styles.contacts}
    >
      <ContentWarning
        message={polyglot.t('employee_assistance.external_links.warning')}
        modalOkText={polyglot.t('employee_assistance.external_links.button.ok')}
        modalCancelText={polyglot.t(
          'employee_assistance.external_links.button.cancel',
        )}
        warningType={isMobile ? WarningTypes.Modal : WarningTypes.Tooltip}
        childFilter={links => setDescriptiveAriaLabels(getExternalLinks(links))}
      >
        <ContactBoxout
          withDropdown={withDropdown}
          dropdownLabel={title}
          id={dropdownId}
          data-testid='contact-boxout'
        >
          {contactOptions?.map((item, index) => (
            <ContactItem
              ariaRole='link'
              action={item.action}
              analyticsEvent={item.analyticsEvent}
              label={item.label}
              icon={item.icon}
              key={`${item.label}-${index}`}
              data-testid='contact-item'
              dataHook={item.hook || item.label}
              isHrefAction={item.isHrefAction}
            />
          ))}
          {hasPhoneNumbers && (
            <ContactListing
              analyticsEvent={phoneAnalytics}
              id={dropdownListId}
              ariaLabelledBy={dropdownId}
              withScroll={mappedPhoneNumbers.length > 3}
              longScroll
              data-testid='contact-listing'
              contactList={mappedPhoneNumbers}
              showFooter={globalPhoneNumbers}
              footerInfo={polyglot.t(
                'employee_assistance.telephone_numbers.footer',
              )}
              footerCtaLabel={polyglot.t(
                'employee_assistance.telephone_numbers.international_numbers',
              )}
              footerCta='/employee-assistance/international-numbers'
            />
          )}
        </ContactBoxout>
      </ContentWarning>
    </ModalityCard>
  );
};

export default ContactListingCard;
